import React, { useState, useEffect } from 'react';
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import './CalendarView.scss';

const CalendarView = ({ 
  selectedDate, 
  onDateSelect, 
  blockedDates = [], 
  availableDates = [],
  reservations = []
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);

  // Convert to Persian date
  const toPersianDate = (date) => {
    const persianMonths = [
      'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
      'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
    ];
    
    const persianDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
    
    // Simple conversion (for production, use a proper library like moment-jalaali)
    const gregorianDate = new Date(date);
    const year = gregorianDate.getFullYear();
    const month = gregorianDate.getMonth();
    const day = gregorianDate.getDate();
    
    // Convert to Persian (simplified)
    const persianYear = year - 621;
    const persianMonth = month < 2 ? month + 10 : month - 2;
    const persianDay = day;
    
    return {
      year: persianYear,
      month: persianMonth,
      day: persianDay,
      monthName: persianMonths[persianMonth],
      dayName: persianDays[gregorianDate.getDay()]
    };
  };

  useEffect(() => {
    generateCalendar();
  }, [currentMonth, blockedDates, availableDates, reservations]);

  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add previous month's trailing days
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthDays - i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        status: getDateStatus(date)
      });
    }

    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const today = new Date();
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = selectedDate && date.toDateString() === new Date(selectedDate).toDateString();
      
      days.push({
        date,
        day,
        isCurrentMonth: true,
        isToday,
        isSelected,
        status: getDateStatus(date)
      });
    }

    // Add next month's leading days
    const totalCells = 42; // 6 weeks * 7 days
    const remainingDays = totalCells - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        status: getDateStatus(date)
      });
    }

    setCalendarDays(days);
  };

  const getDateStatus = (date) => {
    const dateString = date.toISOString().split('T')[0];
    
    // Check if blocked
    if (blockedDates.some(bd => {
      const blockedDate = new Date(bd).toISOString().split('T')[0];
      return blockedDate === dateString;
    })) {
      return 'blocked';
    }

    // Check if has reservations
    const dayReservations = reservations.filter(r => {
      const resDate = new Date(r.date).toISOString().split('T')[0];
      return resDate === dateString;
    });

    if (dayReservations.length > 0) {
      return 'has-reservations';
    }

    // Check if available
    if (availableDates.some(ad => {
      const availDate = new Date(ad).toISOString().split('T')[0];
      return availDate === dateString;
    })) {
      return 'available';
    }

    // Past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) {
      return 'past';
    }

    return 'default';
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day) => {
    if (day.status === 'blocked' || day.status === 'past') return;
    if (onDateSelect) {
      onDateSelect(day.date.toISOString().split('T')[0]);
    }
  };

  const persianDate = toPersianDate(currentMonth);
  const persianDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <button className="calendar-nav-btn" onClick={handlePrevMonth}>
          <FaChevronRight />
        </button>
        <h3 className="calendar-month-year">
          {persianDate.monthName} {persianDate.year}
        </h3>
        <button className="calendar-nav-btn" onClick={handleNextMonth}>
          <FaChevronLeft />
        </button>
      </div>

      <div className="calendar-weekdays">
        {persianDays.map((day, index) => (
          <div key={index} className="weekday">
            {day}
          </div>
        ))}
      </div>

      <div className="calendar-days">
        {calendarDays.map((day, index) => (
          <div
            key={index}
            className={`calendar-day ${day.isCurrentMonth ? 'current-month' : 'other-month'} ${day.status} ${day.isToday ? 'today' : ''} ${day.isSelected ? 'selected' : ''}`}
            onClick={() => handleDateClick(day)}
          >
            {day.day && <span className="day-number">{day.day}</span>}
            {day.status === 'has-reservations' && (
              <span className="reservation-indicator"></span>
            )}
          </div>
        ))}
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color available"></span>
          <span>در دسترس</span>
        </div>
        <div className="legend-item">
          <span className="legend-color has-reservations"></span>
          <span>رزرو شده</span>
        </div>
        <div className="legend-item">
          <span className="legend-color blocked"></span>
          <span>مسدود</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;

